import { JSXElementConstructor, ReactElement } from "react";
import { flushSync } from "react-dom";
import { createRoot } from "react-dom/client";
import uPlot from "uplot";

export type UplotTooltipProps = {
  /**
   * The content shows on the tooltip.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  content: ReactElement<unknown, string | JSXElementConstructor<unknown>> | any;
  /**
   * render the content when the cursor is over the chart.
   */
  renderContent: ({
    container,
    u,
    dataIndex,
  }: {
    /**
     * The container of the tooltip.
     */
    container: HTMLElement;
    /**
     * The uPlot instance.
     */
    u: uPlot;
    /**
     * The index of the data.
     */
    dataIndex: number;
  }) => void;
};

export const uplotTooltipPlugin = ({
  content,
  renderContent,
}: UplotTooltipProps): uPlot.Plugin => {
  let over: HTMLDivElement;
  let tooltip: HTMLDivElement;

  /**
   * Note: To convert the component to HTML in the client side,
   * we need to use flushSync to render the component synchronously
   * instead of using `renderToString` or `renderToStaticMarkup` from react-dom/server.
   *
   * Reference:
   * https://react.dev/reference/react-dom/server/renderToString#removing-rendertostring-from-the-client-code
   */
  function init(u: uPlot) {
    tooltip = document.createElement("div");
    const root = createRoot(tooltip);
    over = u.over;

    setTimeout(() => {
      flushSync(() => {
        root.render(content);
      });
    }, 0);

    tooltip.style.position = "absolute";
    tooltip.style.width = "max-content";
    over.appendChild(tooltip);

    function hideTips() {
      tooltip.style.display = "none";
    }

    function showTips() {
      tooltip.style.display = "block";
    }

    over.addEventListener("mouseleave", () => {
      if (!u.cursor.lock) {
        hideTips();
      }
    });

    over.addEventListener("mouseenter", () => {
      showTips();
    });

    if (u.cursor.left ?? 0 < 0) hideTips();
    else showTips();
  }

  function setCursor(u: uPlot) {
    const { left = 0, top = 0, idx: dataIndex } = u.cursor;
    const container = tooltip.children[0] as HTMLDivElement;

    if (!container) return;
    if (dataIndex == null) {
      container.style.display = "none";
      return;
    }
    container.style.display = "block";
    renderContent({ container, u, dataIndex });

    const canvasWidth = +over.style?.width?.replace("px", "");
    const canvasHeight = +over.style.height.replace("px", "");

    Object.assign(tooltip.style, {
      right: left > canvasWidth / 2 ? `${canvasWidth - left + 10}px` : null,
      left: left > canvasWidth / 2 ? null : `${left + 10}px`,
      top: top > canvasHeight / 2 ? null : `${top + 10}px`,
      bottom: top > canvasHeight / 2 ? `${canvasHeight - top + 10}px` : null,
    });
  }

  return {
    hooks: {
      init,
      setCursor,
    },
  };
};
